import React from "react";
import { Bold, Heading1 } from "lucide-react";

/**
 * iter 386f — Lightweight bulletin text formatting (Jake: "a larger
 * text size and bold text").
 *   **bold text**   → bold
 *   # Line of text  → larger text (whole line)
 * Plain strings stay in Mongo, so old bulletins and every consumer
 * (mobile popup, desktop feed) render unchanged.
 */

function renderInline(line, keyBase) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") && p.length > 4 ? (
      <strong key={`${keyBase}-${i}`} className="font-bold text-white">{p.slice(2, -2)}</strong>
    ) : (
      p
    )
  );
}

export function BulletinMessage({ text, className = "", ...rest }) {
  const lines = (text || "").split("\n");
  return (
    <div className={`whitespace-pre-wrap ${className}`} {...rest}>
      {lines.map((line, i) => {
        if (line.startsWith("# ")) {
          return (
            <div key={i} className="text-lg sm:text-xl font-bold text-white leading-snug">
              {renderInline(line.slice(2), i)}
            </div>
          );
        }
        return <div key={i}>{line ? renderInline(line, i) : "\u00A0"}</div>;
      })}
    </div>
  );
}

/** Plain-text version for one-line previews (strips ** and leading #). */
export function stripBulletinFormatting(text) {
  return (text || "")
    .split("\n")
    .map((l) => (l.startsWith("# ") ? l.slice(2) : l))
    .join(" ")
    .replace(/\*\*([^*]+)\*\*/g, "$1");
}

/** Bold / Large buttons for a bulletin composer textarea. */
export function BulletinFormatToolbar({ value, onChange, textareaRef, hue = "#007AFF" }) {
  const applyBold = () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart ?? value.length;
    const e = el.selectionEnd ?? value.length;
    const sel = value.slice(s, e) || "bold text";
    onChange(value.slice(0, s) + "**" + sel + "**" + value.slice(e));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + 2, s + 2 + sel.length);
    });
  };

  const applyLarge = () => {
    const el = textareaRef.current;
    if (!el) return;
    const s = el.selectionStart ?? value.length;
    const e = el.selectionEnd ?? value.length;
    const lineStart = value.lastIndexOf("\n", s - 1) + 1;
    const lineEndIdx = value.indexOf("\n", e);
    const end = lineEndIdx === -1 ? value.length : lineEndIdx;
    const lines = value.slice(lineStart, end).split("\n");
    const allBig = lines.every((l) => l.startsWith("# "));
    const newBlock = lines
      .map((l) => (allBig ? l.replace(/^# /, "") : l.startsWith("# ") ? l : "# " + l))
      .join("\n");
    onChange(value.slice(0, lineStart) + newBlock + value.slice(end));
    requestAnimationFrame(() => el.focus());
  };

  const btnCls =
    "inline-flex items-center gap-1 px-2 py-1 rounded-[4px] border border-white/[0.1] text-zinc-400 hover:text-white hover:border-white/25 text-[11px] font-semibold transition-colors duration-150";

  return (
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={applyBold} className={btnCls} title="Bold selected text" data-testid="bulletin-format-bold">
        <Bold className="h-3 w-3" /> Bold
      </button>
      <button type="button" onClick={applyLarge} className={btnCls} title="Make line larger" data-testid="bulletin-format-large">
        <Heading1 className="h-3 w-3" /> Large
      </button>
      <span className="text-[10px] text-zinc-600 ml-1 hidden sm:inline">**bold** · start a line with “# ” for large text</span>
    </div>
  );
}
